/**
 * WithdrawalSection - Withdrawal form content
 *
 * Shows:
 * - Card number input
 * - Amount input
 * - Submit button (disabled if below minimum)
 * - Success popup OR error toast
 */

import { useState } from 'react';
import { cn } from '@/lib/utils';
import { useToast, ToastContainer } from '@/components/ui/toast';

// ============================================
// TYPES
// ============================================

export interface WithdrawalSubmitResult {
  success: boolean;
  error?: string;
  minimumRequired?: number;
}

export interface WithdrawalSectionProps {
  /** Minimum withdrawal amount */
  minAmount?: number;
  /** Callback when withdrawal is submitted - returns success/failure */
  onSubmit?: (cardNumber: string, amount: number) => Promise<WithdrawalSubmitResult>;
  /** Callback to go back to landing */
  onBack?: () => void;
}

// ============================================
// AZERBAIJANI ERROR MESSAGES
// ============================================

const ERROR_MESSAGES: Record<string, string> = {
  // Validation errors
  'Invalid card number': 'Kart nömrəsi yanlışdır',
  'Invalid card number. Please check and try again.': 'Kart nömrəsi yanlışdır. Yenidən yoxlayın.',
  'Amount must be positive': 'Məbləğ müsbət olmalıdır',
  'Maximum withdrawal is 5000 AZN': 'Maksimum çıxarış 5000 AZN-dir',

  // Balance/limit errors
  'Insufficient balance': 'Balans kifayət deyil',
  'Withdrawal not allowed': 'Çıxarış mümkün deyil',
  'Please wait 10 minutes between withdrawals.': 'Çıxarışlar arasında 10 dəqiqə gözləyin.',
  'You already have a pending withdrawal. Please wait for it to be processed.': 'Artıq gözləyən çıxarışınız var. Emal olunmasını gözləyin.',

  // System errors
  'User not found. Please start the bot first.': 'İstifadəçi tapılmadı. Əvvəlcə botu başladın.',
  'No game account found. Please make a deposit first.': 'Oyun hesabı tapılmadı. Əvvəlcə depozit edin.',
  'Failed to check balance. Please try again.': 'Balans yoxlanılmadı. Yenidən cəhd edin.',
  'Failed to process withdrawal. Please try again.': 'Çıxarış emal olunmadı. Yenidən cəhd edin.',
  'Failed to initiate withdrawal. Please try again.': 'Çıxarış başladılmadı. Yenidən cəhd edin.',

  // Fallback
  'Withdrawal failed': 'Çıxarış uğursuz oldu',
  'An error occurred. Please try again.': 'Xəta baş verdi. Yenidən cəhd edin.',
};

function translateError(error: string): string {
  // Check for exact match
  if (ERROR_MESSAGES[error]) {
    return ERROR_MESSAGES[error];
  }

  // Check for partial match (for validation messages with dynamic content)
  if (error.includes('Validation failed:')) {
    const parts = error.split(': ');
    const lastPart = parts[parts.length - 1];
    if (lastPart && ERROR_MESSAGES[lastPart]) {
      return ERROR_MESSAGES[lastPart];
    }
  }

  // Check for minimum amount error
  if (error.includes('Minimum withdrawal') || error.includes('minimum')) {
    return 'Minimum çıxarış məbləğinə çatmadınız';
  }

  // Default fallback
  return ERROR_MESSAGES['Withdrawal failed'] ?? 'Çıxarış uğursuz oldu';
}

// ============================================
// COMPONENT
// ============================================

export function WithdrawalSection({
  minAmount = 50,
  onSubmit,
  onBack,
}: WithdrawalSectionProps) {
  const [cardNumber, setCardNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actualMinimum, setActualMinimum] = useState<number | null>(null);
  const { toasts, showError, removeToast } = useToast();

  const numericAmount = parseInt(amount) || 0;
  const isCardValid = cardNumber.replace(/\s/g, '').length >= 16;
  const displayMinimum = actualMinimum ?? minAmount;
  const isAmountValid = numericAmount >= displayMinimum;
  const canSubmit = isCardValid && isAmountValid && !isSubmitting;

  // Format card number with spaces
  const handleCardChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 16);
    const formatted = value.replace(/(\d{4})(?=\d)/g, '$1 ');
    setCardNumber(formatted);
  };

  // Handle amount input
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    setAmount(value);
  };

  const handleSubmit = async () => {
    if (!canSubmit || !onSubmit) return;

    setIsSubmitting(true);

    try {
      const result = await onSubmit(cardNumber.replace(/\s/g, ''), numericAmount);

      if (result.success) {
        setShowSuccess(true);
      } else {
        // Show error toast in Azerbaijani
        const errorMessage = translateError(result.error || 'Withdrawal failed');
        showError(errorMessage);

        // Update minimum if provided
        if (result.minimumRequired) {
          setActualMinimum(result.minimumRequired);
        }
      }
    } catch (err) {
      showError(translateError('An error occurred. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    setCardNumber('');
    setAmount('');
    onBack?.();
  };

  return (
    <div className="withdrawal-section">
      {/* Form */}
      <div className="withdrawal-section__form">
        {/* Card Number Input */}
        <div className="withdrawal-section__field">
          <label className="withdrawal-section__label">Kart nömrəsi</label>
          <input
            type="text"
            className="withdrawal-section__input"
            placeholder="0000 0000 0000 0000"
            value={cardNumber}
            onChange={handleCardChange}
            inputMode="numeric"
          />
        </div>

        {/* Amount Input */}
        <div className="withdrawal-section__field">
          <label className="withdrawal-section__label">
            Məbləğ
            <span className="withdrawal-section__label-hint">
              (min: {displayMinimum} AZN)
            </span>
          </label>
          <div className="withdrawal-section__amount-wrapper">
            <input
              type="text"
              className="withdrawal-section__input withdrawal-section__input--amount"
              placeholder="0"
              value={amount}
              onChange={handleAmountChange}
              inputMode="numeric"
              disabled={isSubmitting}
            />
            <span className="withdrawal-section__currency">AZN</span>
          </div>
        </div>

        {/* Submit Button */}
        <button
          className={cn(
            'withdrawal-section__submit',
            !canSubmit && 'withdrawal-section__submit--disabled'
          )}
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {isSubmitting ? 'Göndərilir...' : 'Çıxarış et'}
        </button>

        {/* Validation hint (only for client-side validation before submit) */}
        {amount && numericAmount < displayMinimum && (
          <p className="withdrawal-section__hint withdrawal-section__hint--error">
            Minimum çıxarış məbləği {displayMinimum} AZN-dir
          </p>
        )}
      </div>

      {/* Toast notifications for errors */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {/* Success Popup */}
      {showSuccess && (
        <div className="withdrawal-popup-overlay">
          <div className="withdrawal-popup">
            <div className="withdrawal-popup__icon">✓</div>
            <h3 className="withdrawal-popup__title">Çıxarış qəbul edildi</h3>
            <p className="withdrawal-popup__message">
              Pulunuz 1-2 saat ərzində kartınıza köçürüləcək.
            </p>
            <button
              className="withdrawal-popup__button"
              onClick={handleSuccessClose}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
